import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import {
  trackMatterRecordBuildClicked,
  trackMatterRecordBuildContinued,
  trackMatterRecordExistingFromModal,
  trackMatterRecordTypeSelected,
} from '@/analytics/utils/matter-record-analytics'
import { useAppSelector } from '@/store/store-hooks'
import { selectActiveMatterId } from '@/matter/store/selectors'
import { matterPaths } from '@/routing/paths'
import { TimelineAssetTabs } from '@/timeline/asset/constants'
import { MedChronAssetTabs } from '@/medical-chronology/asset/constants'
import { MedSummaryAssetTabs } from '@/medical-billing-summaries/asset/constants'
import { selectTimelineAssetForMatter } from '@/timeline/asset/store/selectors'
import { selectMedChronAssetForMatter } from '@/medical-chronology/asset/store/selectors'
import { selectMedSummaryAssetForMatter } from '@/medical-billing-summaries/asset/store/selectors'
import type { MatterRecordSelectionCardType } from '@/matter-asset-common/selection/MatterRecordSelectionCard'
import { requestEditingLease as requestMedChronEditingLease } from '@/medical-chronology/asset/fetch/editing-lease'
import { requestEditingLease as requestTimelineEditingLease } from '@/timeline/asset/fetch/editing-lease'
import { isHeldByAnotherUser } from '@/editing-leases/utils/is-held-by-another-user'

function navigateTo(matterId: string, recordType: MatterRecordSelectionCardType): string {
  switch (recordType) {
    case 'timeline':
      return matterPaths.timeline.tab(matterId, TimelineAssetTabs.ALL_EVENTS)
    case 'chronology':
      return matterPaths.chronology.tab(matterId, MedChronAssetTabs.ALL_EVENTS)
    case 'billing':
      return matterPaths.billing.assetTab(matterId, MedSummaryAssetTabs.ALL_ENTRIES)
  }
}

function navigateToAddFiles(matterId: string, recordType: MatterRecordSelectionCardType): string {
  switch (recordType) {
    case 'timeline':
      return matterPaths.timeline.addFiles(matterId)
    case 'chronology':
      return matterPaths.chronology.addFiles(matterId)
    case 'billing':
      return matterPaths.billing.assetAddFiles(matterId)
  }
}

/**
 * Manages the Build Matter Record modal: open state, routing on selection, and
 * existing-record detection for the modal's chip list.
 *
 * The host renders `BuildMatterRecordModal` with the returned props.
 * Routing is handled here; the modal itself never navigates.
 */
export function useBuildMatterRecordModal(onNavigate?: () => void) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { trackEvent } = useAnalytics()
  const matterId = useAppSelector(selectActiveMatterId)
  // A delayed status read must not navigate after leaving this page or matter.
  const navigationEpoch = useRef(0)
  useEffect(
    () => () => {
      navigationEpoch.current++
    },
    [matterId, location.key]
  )

  const timelineAsset = useAppSelector((state) => selectTimelineAssetForMatter(state, { matterId: matterId ?? undefined }))
  const medChronAsset = useAppSelector((state) => selectMedChronAssetForMatter(state, { matterId: matterId ?? undefined }))
  const medSummaryAsset = useAppSelector((state) => selectMedSummaryAssetForMatter(state, { matterId: matterId ?? undefined }))

  const existingRecordTypes: MatterRecordSelectionCardType[] = [
    ...(timelineAsset ? (['timeline'] as const) : []),
    ...(medChronAsset ? (['chronology'] as const) : []),
    ...(medSummaryAsset ? (['billing'] as const) : []),
  ]

  const handleContinue = useCallback(
    async (recordType: MatterRecordSelectionCardType) => {
      if (!matterId) return
      const epoch = ++navigationEpoch.current
      setOpen(false)
      let destination = navigateToAddFiles(matterId, recordType)
      if (recordType === 'timeline' && !timelineAsset) {
        try {
          const lease = await requestTimelineEditingLease(matterId, 'status')
          if (isHeldByAnotherUser(lease)) destination = navigateTo(matterId, recordType)
        } catch {
          // This read only selects a destination; write endpoints still enforce admission.
        }
      } else if (recordType === 'chronology' && !medChronAsset) {
        try {
          const lease = await requestMedChronEditingLease(matterId, 'status')
          if (isHeldByAnotherUser(lease)) destination = navigateTo(matterId, recordType)
        } catch {
          // This read only selects a destination; write endpoints still enforce admission.
        }
      }
      if (epoch !== navigationEpoch.current) return
      navigate(destination)
      onNavigate?.()
      trackMatterRecordBuildContinued(trackEvent, matterId, recordType)
    },
    [matterId, timelineAsset, medChronAsset, navigate, onNavigate, trackEvent]
  )

  const handleViewExistingRecord = useCallback(
    (recordType: MatterRecordSelectionCardType) => {
      if (!matterId) return
      navigate(navigateTo(matterId, recordType))
      setOpen(false)
      onNavigate?.()
      trackMatterRecordExistingFromModal(trackEvent, matterId, recordType)
    },
    [matterId, navigate, onNavigate, trackEvent]
  )

  const handleRecordTypeSelected = useCallback(
    (recordType: MatterRecordSelectionCardType) => {
      if (!matterId) return
      trackMatterRecordTypeSelected(trackEvent, matterId, recordType)
    },
    [matterId, trackEvent]
  )

  const handleBuildRecord = useCallback(() => {
    setOpen(true)
    if (!matterId) return
    trackMatterRecordBuildClicked(trackEvent, matterId)
  }, [matterId, trackEvent])

  return {
    open,
    onBuildRecord: handleBuildRecord,
    onOpenChange: setOpen,
    onContinue: handleContinue,
    onRecordTypeSelected: handleRecordTypeSelected,
    onViewExistingRecord: handleViewExistingRecord,
    existingRecordTypes,
  }
}
